import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const STORAGE_KEY = "imgnp_cookie_consent_v1";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        const t = setTimeout(() => setVisible(true), 900);
        return () => clearTimeout(t);
      }
    } catch {
      /* localStorage unavailable */
    }
  }, []);

  const decide = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, at: new Date().toISOString() })
      );
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cookie-banner"
          role="dialog"
          aria-label="Cookie consent"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed z-[60] bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md"
        >
          <div className="relative bg-[#0A0A0B]/95 backdrop-blur-xl border border-white/10 p-5 md:p-6 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)]">
            <button
              onClick={() => decide("dismissed")}
              data-testid="cookie-banner-close"
              aria-label="Close"
              className="absolute top-3 right-3 text-zinc-500 hover:text-white transition-colors"
            >
              <X size={16} />
            </button>

            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CCFF00] mb-3">
              {"//"} cookies
            </p>
            <p className="text-sm text-zinc-300 leading-relaxed pr-6">
              We use a minimal set of cookies to make the site work and to understand how
              it&apos;s used. Read the details in our{" "}
              <Link
                to="/privacy"
                onClick={() => setVisible(false)}
                data-testid="cookie-banner-privacy-link"
                className="text-[#CCFF00] hover:underline"
              >
                Privacy Policy
              </Link>
              .
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              <button
                onClick={() => decide("accepted")}
                data-testid="cookie-banner-accept"
                className="bg-[#CCFF00] text-black px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-white transition-colors"
              >
                Accept
              </button>
              <button
                onClick={() => decide("rejected")}
                data-testid="cookie-banner-reject"
                className="border border-white/10 hover:border-[#CCFF00]/50 hover:text-[#CCFF00] text-zinc-300 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
              >
                Reject non-essential
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
